import React, { useEffect, useMemo, useState } from "react";
import { Helmet } from "react-helmet";
import { Link, useLocation } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faArrowUp,
  faEnvelope,
  faPhone,
  faTimes,
} from "@fortawesome/free-solid-svg-icons";
import { SITE_ORIGIN } from "../../config/appConfig";
import { FAVICON_URL, cdnAssetUrl } from "../../config/staticAssets";
import { useMarketingLabels, useMarketingNavLinks } from "./useMarketingLabels";
import PrivacyPolicyBody from "./PrivacyPolicyBody";
import "./TermsAndCondition.css";
import "./UserAgreement.css";

const PAGE_META = {
  title: "Privacy Policy | Welinkjobs",
  description:
    "Users can search for and apply to job opportunities globally through an interactive platform.",
  keywords: "HTML, CSS, JavaScript",
  author: "WeLinkJobs LLC",
  canonicalPath: "/privacy-policy",
  siteName: "Welinkjobs",
  themeColor: "#012660",
};

const MARKETING_STYLESHEETS = [
  "assets/css/bootstrap.min.css",
  "assets/css/fontawesome.css",
  "assets/css/animate.css",
  "assets/css/swiper.min.css",
  "assets/css/odometer.css",
  "assets/css/nice-select.css",
  "assets/css/aos.css",
  "assets/css/jquery-ui.min.css",
  "assets/css/magnific-popup.css",
  "assets/css/rs6.css",
  "assets/css/main.css",
];

function PrivacyPolicy() {
  const labels = useMarketingLabels();
  const navLinks = useMarketingNavLinks("/privacy-policy");
  const { pathname } = useLocation();
  const [menuOpen, setMenuOpen] = useState(false);
  const [showTop, setShowTop] = useState(false);
  const [headerFixed, setHeaderFixed] = useState(false);

  const canonicalUrl = `${SITE_ORIGIN}${PAGE_META.canonicalPath}`;
  const pageUrl = useMemo(() => {
    if (typeof window === "undefined") return canonicalUrl;
    return `${window.location.origin}${pathname}`;
  }, [canonicalUrl, pathname]);

  useEffect(() => {
    const onScroll = () => {
      setShowTop(window.scrollY > 280);
      setHeaderFixed(window.scrollY > 100);
    };
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    document.body.style.overflow = menuOpen ? "hidden" : "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [menuOpen]);

  useEffect(() => {
    const html = document.documentElement;
    const prevTheme = html.getAttribute("data-theme");
    const prevBsTheme = html.getAttribute("data-bs-theme");
    const hadDarkMode = document.body.classList.contains("darkMode");
    const hadLightMode = document.body.classList.contains("lightMode");

    html.setAttribute("data-theme", "light");
    html.setAttribute("data-bs-theme", "light");
    document.body.classList.add("wlj-terms-page-active");
    document.body.classList.remove("darkMode");
    document.body.classList.add("lightMode");

    return () => {
      document.body.classList.remove("wlj-terms-page-active");
      if (prevTheme) html.setAttribute("data-theme", prevTheme);
      else html.removeAttribute("data-theme");
      if (prevBsTheme) html.setAttribute("data-bs-theme", prevBsTheme);
      else html.removeAttribute("data-bs-theme");
      document.body.classList.toggle("darkMode", hadDarkMode);
      document.body.classList.toggle("lightMode", hadLightMode);
    };
  }, []);

  const closeMenu = () => setMenuOpen(false);

  const renderNavLink = (item, onClick) => {
    const content = <span>{item.label}</span>;
    if (item.external) {
      return (
        <a href={item.href} target="_blank" rel="noreferrer" onClick={onClick}>
          {content}
        </a>
      );
    }
    if (item.href.startsWith("/")) {
      return (
        <Link to={item.href} onClick={onClick}>
          {content}
        </Link>
      );
    }
    return (
      <a href={item.href} onClick={onClick}>
        {content}
      </a>
    );
  };

  return (
    <>
      <Helmet htmlAttributes={{ lang: "en" }}>
        <title>{PAGE_META.title}</title>
        <meta name="description" content={PAGE_META.description} />
        <meta name="keywords" content={PAGE_META.keywords} />
        <meta name="author" content={PAGE_META.author} />
        <meta name="viewport" content="width=device-width, initial-scale=1.0" />
        <meta httpEquiv="X-UA-Compatible" content="IE=edge" />
        <meta name="theme-color" content={PAGE_META.themeColor} />
        <meta name="robots" content="index, follow" />
        <link rel="canonical" href={canonicalUrl} />
        <link rel="image_src" href={FAVICON_URL} />
        <link rel="shortcut icon" href={FAVICON_URL} type="image/x-icon" />
        <link rel="icon" href={FAVICON_URL} />
        {MARKETING_STYLESHEETS.map((path) => (
          <link
            key={path}
            rel="stylesheet"
            href={cdnAssetUrl(path)}
            data-marketing-style={path}
          />
        ))}
        <meta property="og:title" content={PAGE_META.title} />
        <meta property="og:description" content={PAGE_META.description} />
        <meta property="og:url" content={pageUrl} />
        <meta property="og:type" content="website" />
        <meta property="og:site_name" content={PAGE_META.siteName} />
        <meta property="og:image" content={FAVICON_URL} />
        <meta name="twitter:card" content="summary" />
        <meta name="twitter:title" content={PAGE_META.title} />
        <meta name="twitter:description" content={PAGE_META.description} />
      </Helmet>

      <div className={`wlj-terms-backtotop ${showTop ? "is-visible" : ""}`}>
        <button type="button" onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}>
          <FontAwesomeIcon icon={faArrowUp} />
        </button>
      </div>

      <div className={`wlj-terms-page ${headerFixed ? "wlj-header-fixed-page" : ""}`}>
        <header className={`wlj-terms-header ${headerFixed ? "is-fixed" : ""}`}>
          <div className="wlj-header-inner">
            <div className="wlj-container wlj-mxw-1710">
              <div className="wlj-ul-between wlj-header-bar">
                <div className="wlj-header-logo">
                  <Link to="/about">
                    <img src={cdnAssetUrl("assets/welink.79ac1260.svg")} alt="Logo" />
                  </Link>
                </div>

                <div className="wlj-header-nav-wrap">
                  <nav className="wlj-header-nav">
                    <ul>
                      {navLinks.map((item) => (
                        <li key={item.label} className={item.active ? "active" : ""}>
                          {renderNavLink(item)}
                        </li>
                      ))}
                    </ul>
                  </nav>
                </div>

                <div className="wlj-header-actions">
                  <Link className="wlj-btn" to="/NewsFeed">{labels.explore}</Link>
                  <Link className="wlj-btn wlj-btn--ghost" to="/SignIn">{labels.loginSignup}</Link>
                </div>

                <button type="button" className="wlj-hamburger" onClick={() => setMenuOpen(true)} aria-label={labels.openMenu}>
                  <span className="wlj-hamburger-icon" />
                </button>
              </div>
            </div>
          </div>

          <div className={`wlj-mobile-menu ${menuOpen ? "is-open" : ""}`}>
            <div className="wlj-mobile-menu-scroll">
              <button type="button" className="wlj-mobile-close" onClick={closeMenu}>
                <FontAwesomeIcon icon={faTimes} />
              </button>
              <div className="wlj-mobile-logo">
                <Link to="/about" onClick={closeMenu}>
                  <img src={cdnAssetUrl("assets/welink.79ac1260.svg")} alt="Logo" />
                </Link>
              </div>
              <ul className="wlj-mobile-nav">
                {navLinks.map((item) => (
                  <li key={item.label} className={item.active ? "active" : ""}>
                    {renderNavLink(item, closeMenu)}
                  </li>
                ))}
                <li>
                  <Link to="/NewsFeed" onClick={closeMenu}>{labels.explore}</Link>
                </li>
                <li>
                  <Link to="/SignIn" onClick={closeMenu}>{labels.loginSignup}</Link>
                </li>
              </ul>
            </div>
          </div>
          <div
            className={`wlj-mobile-backdrop ${menuOpen ? "is-open" : ""}`}
            onClick={closeMenu}
            aria-hidden="true"
          />
        </header>

        <div className="wlj-terms-body-overlay" />

        <main className="wlj-terms-main">
          <section className="wlj-feature">
            <div className="wlj-container wlj-pt-50">
              <div className="wlj-row">
                <div className="wlj-col-md-12">
                  <div className="wlj-breadcrumb-content">
                    <h2 className="wlj-page-title">{labels.privacyPolicy}</h2>
                  </div>
                </div>
              </div>
            </div>

            <div className="wlj-container">
              <div className="wlj-content-wrap">
                <div className="wlj-col-xl-12 wlj-col-lg-8">
                  <PrivacyPolicyBody />
                </div>
              </div>
            </div>
          </section>
        </main>

        <footer className="wlj-terms-footer wlj-pt-80 wlj-pb-25">
          <div className="wlj-container">
            <div
              className="wlj-footer-cta"
              style={{
                "--wlj-cta-shadow": `url(${cdnAssetUrl("assets/img/shape/cta_shadow.png")})`,
              }}
            >
              <a href="mailto:info@welinkjobs.com" className="wlj-footer-cta-item wlj-mt-30">
                <span className="wlj-footer-icon wlj-mr-35" aria-hidden="true">
                  <FontAwesomeIcon icon={faEnvelope} />
                </span>
                <span>
                  <span className="wlj-footer-cta-label">{labels.dropNote}</span>
                  <h3 className="wlj-footer-cta-value">info@welinkjobs.com</h3>
                </span>
              </a>

              <a href="tel:+13072436345" className="wlj-footer-cta-item wlj-mt-30">
                <span className="wlj-footer-icon wlj-mr-35" aria-hidden="true">
                  <FontAwesomeIcon icon={faPhone} />
                </span>
                <span>
                  <span className="wlj-footer-cta-label">{labels.callDirectly}</span>
                  <h3 className="wlj-footer-cta-value">+1 3072436345</h3>
                </span>
              </a>

              <span className="wlj-footer-cta-line" aria-hidden="true" />
            </div>

            <div className="wlj-footer-links wlj-mt-12">
              <div className="wlj-mt-none-40 wlj-flex-between">
                <div className="wlj-footer-col wlj-mt-40">
                  <ul>
                    <li>
                      <Link to="/about">{labels.aboutUs}</Link>
                    </li>
                    <li>
                      <Link to="/about#contactform">{labels.contactUs}</Link>
                    </li>
                  </ul>
                </div>
                <div className="wlj-footer-col wlj-mt-40">
                  <ul>
                    <li>
                      <Link to="/privacy-policy">{labels.privacyPolicy}</Link>
                    </li>
                    <li>
                      <Link to="/faq">{labels.faqs}</Link>
                    </li>
                  </ul>
                </div>
                <div className="wlj-footer-col wlj-mt-40">
                  <ul>
                    <li>
                      <Link to="/terms-and-conditions">{labels.terms}</Link>
                    </li>
                    <li>
                      <Link to="/user-agreement">{labels.userAgreement}</Link>
                    </li>
                  </ul>
                </div>
              </div>
            </div>

            <div className="wlj-footer-copy wlj-mt-12">
              <p className="wlj-mt-15">
                Copyright © <a href="https://welinkjobs.com/">Welinkjobs LLC</a>. {labels.allRightsReserved}
              </p>
            </div>
          </div>

          <div className="wlj-footer-shapes" aria-hidden="true">
            <div className="wlj-shape wlj-shape--1">
              <img src={cdnAssetUrl("assets/img/shape/ft_shape1.png")} alt="" />
            </div>
            <div className="wlj-shape wlj-shape--2">
              <img src={cdnAssetUrl("assets/img/shape/ft_shape2.png")} alt="" />
            </div>
          </div>
        </footer>
      </div>
    </>
  );
}

export default PrivacyPolicy;
